import {makeObservable, flow, observable, computed, action, flowResult} from 'mobx'
import {RootStore} from './RootStore'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'

export type CommunicationPreference = {
  label: string
  name: string
  value: boolean | string
  validUntil?: string
}

export type CommunicationPreferencesResult = {
  result: {marketingChannels: CommunicationPreference[]}
  error: boolean
}

export enum CommunicationPreferencesType {
  GLOBAL_PREFERENCE = 'GLOBAL_PREFERENCE',
  CHANNEL_PREFERENCE = 'CHANNEL_PREFERENCE'
}

export enum ChannelConsent {
  OPT_IN = 'Opt In',
  OPT_OUT = 'Opt Out',
  NOT_SET = 'Not Set'
}

export class MarketingPreferencesStore {
  rootStore: RootStore
  communicationPreferences: CommunicationPreference[]
  communicationPreferencesSchema: CommunicationPreference[]
  communicationPreferencesType: CommunicationPreferencesType
  isLoadingCommunicationPreferences: boolean
  communicationPreferencesError: boolean
  communicationPreferencesErrorMessage: string | null
  communicationPreferencesUnavailable: boolean
  hasUnsavedCommunicationPreferences: boolean
  preferenceExpired: boolean
  preferenceInExpiryWindow: boolean

  constructor(
    rootStore: RootStore,
    initialState = {
      communicationPreferences: [],
      communicationPreferencesSchema: [],
      communicationPreferencesType: CommunicationPreferencesType.GLOBAL_PREFERENCE,
      isLoadingCommunicationPreferences: false,
      communicationPreferencesError: false,
      communicationPreferencesErrorMessage: null,
      communicationPreferencesUnavailable: false,
      hasUnsavedCommunicationPreferences: true,
      preferenceExpired: false,
      preferenceInExpiryWindow: false
    }
  ) {
    this.communicationPreferences = initialState.communicationPreferences
    this.communicationPreferencesSchema = initialState.communicationPreferencesSchema
    this.communicationPreferencesType = initialState.communicationPreferencesType
    this.isLoadingCommunicationPreferences = initialState.isLoadingCommunicationPreferences
    this.communicationPreferencesError = initialState.communicationPreferencesError
    this.communicationPreferencesErrorMessage = initialState.communicationPreferencesErrorMessage
    this.communicationPreferencesUnavailable = initialState.communicationPreferencesUnavailable
    this.hasUnsavedCommunicationPreferences = initialState.hasUnsavedCommunicationPreferences
    this.preferenceExpired = initialState.preferenceExpired
    this.preferenceInExpiryWindow = initialState.preferenceInExpiryWindow

    makeObservable(this, {
      communicationPreferences: observable,
      communicationPreferencesSchema: observable,
      communicationPreferencesType: observable,
      isLoadingCommunicationPreferences: observable,
      communicationPreferencesError: observable,
      communicationPreferencesErrorMessage: observable,
      communicationPreferencesUnavailable: observable,
      hasUnsavedCommunicationPreferences: observable,
      preferenceExpired: observable,
      preferenceInExpiryWindow: observable,
      updateCommunicationPreferences: flow.bound,
      getCommunicationPreferences: flow.bound,
      getCommunicationPreferencesSchema: flow.bound,
      checkIfInExpiryWindow: flow.bound,
      setCommunicationPreferencesSchema: action.bound,
      setCommunicationPreferences: action.bound,
      setCommunicationPreferencesType: action.bound,
      setIsLoadingCommunicationPreferences: action.bound,
      setCommunicationPreferencesError: action.bound,
      setCommunicationPreferencesErrorMessage: action.bound,
      setCommunicationPreferencesUnavailable: action.bound,
      setHasUnsavedCommunicationPreferences: action.bound,
      setPreferenceExpired: action.bound,
      setPreferenceInExpiryWindow: action.bound,
      fetchCommunicationPreferences: flow.bound,
      saveCommunicationPreferences: flow.bound,
      toggleCommunicationPreference: action.bound,
      preferenceNeverSet: computed
    })

    this.rootStore = rootStore
  }

  get asJson() {
    return {
      communicationPreferences: this.communicationPreferences,
      communicationPreferencesSchema: this.communicationPreferencesSchema,
      communicationPreferencesType: this.communicationPreferencesType,
      isLoadingCommunicationPreferences: this.isLoadingCommunicationPreferences,
      communicationPreferencesError: this.communicationPreferencesError,
      communicationPreferencesErrorMessage: this.communicationPreferencesErrorMessage,
      communicationPreferencesUnavailable: this.communicationPreferencesUnavailable,
      hasUnsavedCommunicationPreferences: this.hasUnsavedCommunicationPreferences,
      preferenceExpired: this.preferenceExpired,
      preferenceInExpiryWindow: this.preferenceInExpiryWindow
    }
  }

  // Getters
  get preferenceNeverSet() {
    return this.communicationPreferences.some((pref) => pref.value === "Not Set")
  }

  // Setters
  setCommunicationPreferencesSchema(value: CommunicationPreference[]) {
    this.communicationPreferencesSchema = value
  }

  setCommunicationPreferences(value: CommunicationPreference[]) {
    this.communicationPreferences = value
  }

  setCommunicationPreferencesType(value: CommunicationPreferencesType) {
    this.communicationPreferencesType = value
  }

  setIsLoadingCommunicationPreferences(value: boolean) {
    this.isLoadingCommunicationPreferences = value
  }

  setCommunicationPreferencesError(value: boolean) {
    this.communicationPreferencesError = value
  }

  setCommunicationPreferencesErrorMessage(value: string | null) {
    this.communicationPreferencesErrorMessage = value
  }

  setCommunicationPreferencesUnavailable(value: boolean) {
    this.communicationPreferencesUnavailable = value
  }

  setHasUnsavedCommunicationPreferences(value: boolean) {
    this.hasUnsavedCommunicationPreferences = value
  }

  setPreferenceExpired(value: boolean) {
    this.preferenceExpired = value
  }

  setPreferenceInExpiryWindow(value: boolean) {
    this.preferenceInExpiryWindow = value
  }

  *updateCommunicationPreferences(prefArray: CommunicationPreference[] = []) {
    const res: Response = yield fetch(
      `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Account-SetCommunicationPreferences`,
      {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify({
          marketingChannels: prefArray.map((pref) => ({
            name: pref.name,
            value: pref.value
          }))
        })
      }
    )

    // Check for an error with the request
    if (!res.ok) {
      throw new Error('Error during setting communication preferences')
    }

    // Check the return data in case there was an error on backend
    const json: {error: boolean} = yield res.json()
    if (json.error) {
      throw new Error('Error during setting communication preferences')
    }
  }

  /**
   * Get communications preferences schema for account registration
   * @returns an array of communication preferences with their labels, names, and values
   */
  *getCommunicationPreferencesSchema() {
    let returnVal: CommunicationPreference[] = []

    const res: Response = yield fetch(
      `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Account-CommunicationPreferencesSchema`,
      {
        credentials: 'include'
      }
    )

    if (!res.ok) {
      throw new Error('Unable to fetch communication preferences schema')
    }

    const json: CommunicationPreferencesResult = yield res.json()

    if (json.error) {
      throw new Error(`Communications preferences error`)
    }

    // Non-error results should always be provided as error of communications preferences.
    returnVal = json.result.marketingChannels as CommunicationPreference[]

    this.setCommunicationPreferencesSchema(returnVal)

    return returnVal
  }

  /**
   * Get customer communications preferences - populates the communicationPreferences
   * property
   */
  *getCommunicationPreferences() {
    const res: Response = yield fetch(
      `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Account-CommunicationPreferences`,
      {
        credentials: 'include'
      }
    )

    if (!res.ok) {
      throw new Error('Unable to fetch communication preferences')
    }

    const json: CommunicationPreferencesResult = yield res.json()

    if (json.error) {
      throw new Error(`Communications preferences error`)
    }

    this.setCommunicationPreferences(json.result.marketingChannels as CommunicationPreference[])
  }

  *fetchCommunicationPreferences() {
    // Load the customer preferences and refresh expiry state.
    this.setIsLoadingCommunicationPreferences(true)
    try {
      yield flowResult(this.getCommunicationPreferences())
      if (this.communicationPreferencesType === CommunicationPreferencesType.CHANNEL_PREFERENCE) {
        yield flowResult(this.checkIfPreferenceExpired())
        yield flowResult(this.checkIfInExpiryWindow())
      }
      this.setCommunicationPreferencesError(false)
      this.setCommunicationPreferencesErrorMessage(null)
    } catch (error) {
      console.error('Failed to fetch communication preferences', error)
      this.setCommunicationPreferencesError(true)
      this.setCommunicationPreferencesErrorMessage('Failed to fetch communication preferences')
    } finally {
      this.setIsLoadingCommunicationPreferences(false)
    }
  }

  *saveCommunicationPreferences(preferences: CommunicationPreference[]) {
    // Persist changed preferences to the backend and keep local state in sync.
    try {
      yield flowResult(this.updateCommunicationPreferences(preferences))
      this.setHasUnsavedCommunicationPreferences(false)
      this.setCommunicationPreferencesError(false)
      this.setCommunicationPreferencesErrorMessage(null)
      this.setPreferenceExpired(false)
      this.setPreferenceInExpiryWindow(false)
      return true
    } catch (error) {
      console.error('Error updating communication preferences:', error)
      this.setCommunicationPreferencesError(true)
      this.setCommunicationPreferencesErrorMessage('Failed to save preferences')
      // Re-throw the error so the Component can catch and handle
      throw error
    }
  }

  toggleCommunicationPreference(name: string, newValue: 'Opt In' | 'Opt Out') {
    // Toggle a single preference locally before save.
    this.setCommunicationPreferences(
      this.communicationPreferences.map((pref) =>
        pref.name === name && pref.value !== newValue ? {...pref, value: newValue} : pref
      )
    )
    this.setHasUnsavedCommunicationPreferences(true)
  }

  private checkIfPreferenceExpired() {
    try {
      for (const pref of this.communicationPreferences) {
        if (pref.validUntil) {
          const validUntilDate = new Date(pref.validUntil)
          const now = new Date()
          if (now >= validUntilDate) {
            this.setPreferenceExpired(true)
          }
        }
      }
    } catch (error) {
      console.error('Store: Error checking preference expiry:', error)
      this.setPreferenceExpired(false)
    }
  }

  *checkIfInExpiryWindow() {
    try {
      const reminderDays = (yield flowResult(
        this.rootStore.globalStore.getCustomSitePreferenceById(
          'communicationPreferencesExpiryReminder'
        )
      )) as number

      const reminderMs = reminderDays * 24 * 60 * 60 * 1000

      for (const pref of this.communicationPreferences) {
        if (pref.validUntil) {
          const validUntilDate = new Date(pref.validUntil)
          const now = new Date().getTime()
          const reminderWindowStart = new Date(validUntilDate).getTime() - reminderMs

          if (now >= reminderWindowStart && now <= validUntilDate.getTime()) {
            this.setPreferenceInExpiryWindow(true)
          }
        }
      }
    } catch (error) {
      console.error('Store: Error checking if preferences are in expiry window:', error)
      this.setPreferenceInExpiryWindow(false)
    }
  }
}
